import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Loading } from '@1trading/react/components/ui/loading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BUSINESS_TYPES } from '@/constants/institution';
import { InstitutionCompanyFieldsetRegisteredAddress } from '@/features/institution/company/components/fieldsets/registered-address';
import {
  type InstitutionCompany,
  type InstitutionCompanyOrDefaults,
  institutionCompanySchema,
  institutionCompanyToInstitutionDetailsCompanyApi,
  institutionDetailsCompanyApiToInstitutionCompany,
} from '@/features/institution/company/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMutation } from '@/hooks/queries/use-profile-institution-details';

const InstitutionCompanyFormForm = ({ defaultValues }: { defaultValues: InstitutionCompanyOrDefaults }) => {
  const form = useForm<InstitutionCompany>({
    defaultValues: {
      ...defaultValues,
      companyName: defaultValues?.companyName ?? '',
      taxId: defaultValues?.taxId ?? '',
      leiNumber: defaultValues?.leiNumber ?? '',
      address: defaultValues?.address ?? '',
      address2: defaultValues?.address2 ?? '',
      postCode: defaultValues?.postCode ?? '',
      city: defaultValues?.city ?? '',
      country: defaultValues?.country ?? '',
    },
    resolver: standardSchemaResolver(institutionCompanySchema),
  });
  const { isPending, mutate } = useProfileInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: InstitutionCompany) => {
    mutate(
      { company: institutionCompanyToInstitutionDetailsCompanyApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate('/institution/onboarding/regulatory-information', { replace: true });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form name="institution-company" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-6">
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('institutionDetails.company.companyName')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.company.placeholders.companyName')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <InstitutionCompanyFieldsetRegisteredAddress />
          <FormField
            name="typeOfBusiness"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.company.typeOfBusiness.title')}*</FormLabel>
                <Select defaultValue={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('institutionDetails.company.placeholders.typeOfBusiness')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {BUSINESS_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`institutionDetails.company.typeOfBusiness.options.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="taxId"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('institutionDetails.company.taxId')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.company.placeholders.taxId')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="leiNumber"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('institutionDetails.company.leiNumber.title')}</FormLabel>
                <FormDescription>{t('institutionDetails.company.leiNumber.description')}</FormDescription>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.company.placeholders.leiNumber')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.next')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
InstitutionCompanyFormForm.displayName = 'InstitutionCompanyFormForm';

const InstitutionCompanyForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'company');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const handlePrevStep = useCallback(() => {
    navigate('/institution/onboarding/branch-operations', { replace: true });
  }, [navigate]);

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.company ? institutionDetailsCompanyApiToInstitutionCompany(institutionDetailsAnswers.company) : {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.company.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.company.description')}</PageHeadingDescription>
      </PageHeading>
      <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />
      <PageCard size="lg" bordered fixedFooter>
        <InstitutionCompanyFormForm defaultValues={defaultValues} />
      </PageCard>
    </section>
  );
};
InstitutionCompanyForm.displayName = 'InstitutionCompanyForm';

export default InstitutionCompanyForm;
